import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { WifiOff, Wifi } from 'lucide-react';
import { useOnlineStatus } from '@/lib/useOnlineStatus';
import { fa } from '@/i18n/fa';

/**
 * A slim, non-blocking banner reacting to connectivity changes. Stays
 * visible the whole time the device is offline (so the user always knows
 * data may be stale) and flashes a brief confirmation once back online.
 */
export function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const [showBackOnline, setShowBackOnline] = useState(false);
  // "Adjusting state when a prop changes" (React docs): derive the
  // transition during render instead of a setState-in-effect, which would
  // otherwise trigger an avoidable extra render on every online/offline flip.
  const [prevIsOnline, setPrevIsOnline] = useState(isOnline);
  if (isOnline !== prevIsOnline) {
    setPrevIsOnline(isOnline);
    setShowBackOnline(isOnline);
  }

  // The auto-hide timer is a genuine effect (a subscription to the passage
  // of time), so setState belongs in its callback, not the effect body.
  useEffect(() => {
    if (!showBackOnline) return;
    const timeout = window.setTimeout(() => setShowBackOnline(false), 2500);
    return () => window.clearTimeout(timeout);
  }, [showBackOnline]);

  return (
    <AnimatePresence initial={false}>
      {!isOnline && (
        <motion.div
          key="offline"
          role="status"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="flex items-center justify-center gap-2 bg-warning/15 px-4 py-2 text-center text-xs font-medium text-text">
            <WifiOff className="size-3.5 shrink-0 text-warning" aria-hidden="true" />
            <span>{fa.offline.title}</span>
          </div>
        </motion.div>
      )}
      {isOnline && showBackOnline && (
        <motion.div
          key="online"
          role="status"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="flex items-center justify-center gap-2 bg-success/15 px-4 py-2 text-center text-xs font-medium text-text">
            <Wifi className="size-3.5 shrink-0 text-success" aria-hidden="true" />
            <span>{fa.offline.backOnline}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
